import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useNavbar } from "../hooks/useNavbar";
import { useLanguage } from "../context/LanguageContext";
import { t } from "../i18n";

const API = process.env.REACT_APP_API_URL || "";

const STATUS_CONFIG = {
  Pending:       { color: "#92400e", bg: "#fef3c7", dot: "#f59e0b" },
  Approved:      { color: "#065f46", bg: "#d1fae5", dot: "#10b981" },
  Confirmed:     { color: "#065f46", bg: "#d1fae5", dot: "#10b981" },
  CheckedIn:     { color: "#1e40af", bg: "#dbeafe", dot: "#3b82f6" },
  CheckedOut:    { color: "#374151", bg: "#f3f4f6", dot: "#9ca3af" },
  PendingCancel: { color: "#6b21a8", bg: "#f3e8ff", dot: "#a855f7" },
  Cancelled:     { color: "#991b1b", bg: "#fee2e2", dot: "#ef4444" },
};

const ALL_TAB = { color: "#111827", bg: "#f3f4f6", dot: "#9ca3af" };

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString("th-TH", { day: "2-digit", month: "short", year: "numeric" }) : "-";

const fmtDateTime = (d) =>
  d ? new Date(d).toLocaleString("th-TH", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "-";

/* ── ตัวช่วยเรื่องวัน (ใช้เวลาท้องถิ่น ไม่ใช่ UTC เพื่อให้ "วันนี้" ตรงกับที่ผู้ใช้เห็น) ── */
const pad2      = (n) => String(n).padStart(2, "0");
const dayKey    = (d) => { const x = new Date(d); return `${x.getFullYear()}-${pad2(x.getMonth() + 1)}-${pad2(x.getDate())}`; };
const monthKey  = (d) => dayKey(d).slice(0, 7);
const todayKey  = dayKey(new Date());
const fmtMonth  = (key) => {
  const [y, m] = key.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString("th-TH", { month: "long", year: "numeric" });
};

const DATE_MODES = [["All", "dateAll"], ["Day", "dateDay"], ["Month", "dateMonth"], ["Range", "dateRange"]];

export default function StatusPage() {
  const NavbarComponent = useNavbar();
  const { lang } = useLanguage();

  const [bookings,     setBookings]     = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState(null);
  const [activeFilter, setActiveFilter] = useState("All");
  const [search,       setSearch]       = useState("");
  const [dateMode,     setDateMode]     = useState("All");                 // "All" | "Day" | "Month"
  const [dayValue,     setDayValue]     = useState(todayKey);
  const [monthValue,   setMonthValue]   = useState(todayKey.slice(0, 7));
  const [fromValue,    setFromValue]    = useState(todayKey);
  const [toValue,      setToValue]      = useState(todayKey);
  const navigate = useNavigate();

  const role = localStorage.getItem("role"); // "user" | "staff" | "admin"
  const isStaff = role === "staff" || role === "admin";

  useEffect(() => {
    const fetchBookings = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem("token");
        if (!token) { setError("กรุณาเข้าสู่ระบบก่อน"); setLoading(false); return; }

        let list = [];

        if (isStaff) {
          const candidates = [
            `${API}/api/bookings`,
            `${API}/api/admin/bookings`,
            `${API}/api/bookings/all`,
          ];

          let loaded = false;
          let lastErr = "";
          for (const url of candidates) {
            try {
              const res = await fetch(url, {
                headers: { Authorization: `Bearer ${token}` },
              });
              if (res.ok) {
                const data = await res.json();
                list = Array.isArray(data)
                  ? data
                  : Array.isArray(data?.bookings) ? data.bookings : [];
                loaded = true;
                break;
              } else {
                const body = await res.json().catch(() => ({}));
                lastErr = `[${url.replace(`${API}/api/`,"")}] HTTP ${res.status}: ${body?.message || res.statusText}`;
              }
            } catch (e) {
              lastErr = e.message;
            }
          }

          if (!loaded) {
            const res = await fetch(`${API}/api/bookings/my`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            if (res.ok) {
              const data = await res.json();
              list = Array.isArray(data) ? data : [];
            } else {
              throw new Error(`โหลดไม่สำเร็จ: ${lastErr}`);
            }
          }
        } else {
          const res = await fetch(`${API}/api/bookings/my`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (!res.ok) {
            const body = await res.json().catch(() => ({}));
            throw new Error(`HTTP ${res.status}: ${body?.message || "ไม่สามารถโหลดข้อมูลได้"}`);
          }
          const data = await res.json();
          list = Array.isArray(data) ? data : [];
        }
        setBookings([...list].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)));
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchBookings();
  }, [isStaff]);

  const goToDetail = (id) => navigate(`/booking/${id}`);

  /* กรองช่วงเวลา (ตามวันที่จอง) ก่อน เพื่อให้ตัวเลขบนแท็บสถานะตรงกับช่วงที่เลือก */
  const periodBookings = bookings.filter((b) => {
    if (dateMode === "All") return true;
    if (!b.createdAt) return false;
    const k = dayKey(b.createdAt);
    if (dateMode === "Day")   return k === dayValue;
    if (dateMode === "Month") return monthKey(b.createdAt) === monthValue;
    // yyyy-mm-dd เทียบแบบ string ได้ตรงตามลำดับเวลา / สลับให้ถ้าผู้ใช้เลือกวันเริ่มหลังวันจบ
    const [from, to] = fromValue <= toValue ? [fromValue, toValue] : [toValue, fromValue];
    return k >= from && k <= to;
  });

  // เดือนที่มีรายการจริง + เดือนปัจจุบันเสมอ (ใหม่ → เก่า)
  const monthOptions = Array.from(new Set([
    ...bookings.filter((b) => b.createdAt).map((b) => monthKey(b.createdAt)),
    todayKey.slice(0, 7),
    monthValue,
  ])).sort().reverse();

  const countFor = (key) =>
    key === "All" ? periodBookings.length : periodBookings.filter((b) => b.status === key).length;

  const filteredBookings = periodBookings
    .filter((b) => activeFilter === "All" || b.status === activeFilter)
    .filter((b) => {
      if (!search.trim()) return true;
      const q = search.toLowerCase();
      if (isStaff) {
        const name  = `${b.user?.firstname ?? ""} ${b.user?.lastname ?? ""}`.trim().toLowerCase();
        const email = (b.user?.email ?? "").toLowerCase();
        const room  = (b.roomUnit?.roomNumber ?? "").toLowerCase();
        const guest = (b.guestName ?? "").toLowerCase();
        return name.includes(q) || email.includes(q) || room.includes(q) || guest.includes(q);
      } else {
        const room     = (b.roomUnit?.roomNumber ?? "").toLowerCase();
        const roomType = (b.roomType?.name ?? "").toLowerCase();
        return room.includes(q) || roomType.includes(q);
      }
    });

  const tabEntries = [["All", ALL_TAB], ...Object.entries(STATUS_CONFIG)];

  // ✅ return ครอบด้วย Fragment และลบ <Navbar /> ที่ซ้ำออก
  return (
    <>
      <style>{`
        @media (max-width: 600px) {
          .sp-booking-item { flex-direction: column !important; gap: 12px !important; }
          .sp-img-wrap     { width: 100% !important; height: 160px !important; }
          .sp-status-col   { flex-direction: row !important; align-items: center !important;
                             justify-content: space-between !important; min-height: auto !important; }
        }
      `}</style>
      <NavbarComponent />
      <div style={styles.page}>
        <main style={styles.main}>
          <div style={styles.card}>

            {/* ── Header ── */}
            <div style={styles.cardHeader}>
              <span style={{ fontSize: 22 }}>🔔</span>
              <h2 style={styles.cardTitle}>
                {isStaff ? t(lang,"allBookings") : t(lang,"myBookings")}
              </h2>
              {isStaff && !loading && (
                <span style={styles.totalBadge}>{periodBookings.length} {t(lang,"items")}</span>
              )}
            </div>
            <div style={styles.divider} />

            {/* ── Search bar ── */}
            {!loading && !error && (
              <div style={styles.searchWrap}>
                <svg style={styles.searchIcon} width="14" height="14" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                </svg>
                <input
                  style={styles.searchInput}
                  placeholder={isStaff ? t(lang,"searchStaff") : t(lang,"searchUser")}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                {search && (
                  <button style={styles.searchClear} onClick={() => setSearch("")}>✕</button>
                )}
              </div>
            )}

            {/* ── Date filter : วัน / เดือน / ทั้งหมด (ค่าเริ่มต้น = ทั้งหมด) ── */}
            {!loading && !error && (
              <div style={styles.dateBar}>
                <span style={styles.dateBarLabel}>{t(lang,"dateFilterBy")}</span>
                {DATE_MODES.map(([mode, key]) => {
                  const isActive = dateMode === mode;
                  return (
                    <button
                      key={mode}
                      onClick={() => setDateMode(mode)}
                      style={{
                        ...styles.filterTab,
                        background:  isActive ? "#111827" : "#fff",
                        color:       isActive ? "#fff"    : "#6b7280",
                        borderColor: isActive ? "#111827" : "#e5e7eb",
                        fontWeight:  isActive ? 600 : 400,
                      }}
                    >
                      {t(lang, key)}
                    </button>
                  );
                })}

                {dateMode === "Day" && (
                  <input
                    type="date"
                    style={styles.dateInput}
                    value={dayValue}
                    onChange={(e) => setDayValue(e.target.value || todayKey)}
                  />
                )}

                {dateMode === "Month" && (
                  <select
                    style={styles.dateInput}
                    value={monthValue}
                    onChange={(e) => setMonthValue(e.target.value)}
                  >
                    {monthOptions.map((m) => (
                      <option key={m} value={m}>{fmtMonth(m)}</option>
                    ))}
                  </select>
                )}

                {dateMode === "Range" && (
                  <>
                    <input
                      type="date"
                      style={styles.dateInput}
                      value={fromValue}
                      max={toValue}
                      onChange={(e) => setFromValue(e.target.value || todayKey)}
                    />
                    <span style={styles.dateBarLabel}>{t(lang,"dateTo")}</span>
                    <input
                      type="date"
                      style={styles.dateInput}
                      value={toValue}
                      min={fromValue}
                      onChange={(e) => setToValue(e.target.value || todayKey)}
                    />
                  </>
                )}
              </div>
            )}

            {/* ── Filter Tabs ── */}
            {!loading && !error && (
              <div style={styles.filterBar}>
                {tabEntries.map(([key, cfg]) => {
                  const count = countFor(key);
                  const hideWhenEmpty = !isStaff && (key === "PendingCancel" || key === "Cancelled");
                  if (hideWhenEmpty && count === 0) return null;
                  const isActive = activeFilter === key;
                  return (
                    <button
                      key={key}
                      onClick={() => setActiveFilter(key)}
                      style={{
                        ...styles.filterTab,
                        background:  isActive ? "#111827" : "#fff",
                        color:       isActive ? "#fff"    : "#6b7280",
                        borderColor: isActive ? "#111827" : "#e5e7eb",
                      }}
                    >
                      {key !== "All" && (
                        <span style={{ ...styles.filterDot, background: isActive ? "#fff" : cfg.dot }} />
                      )}
                      {key === "All" ? t(lang,"allStatus") : t(lang, key)}
                      <span style={{
                        ...styles.filterCount,
                        background: isActive ? "rgba(255,255,255,.2)" : "#f3f4f6",
                        color:      isActive ? "#fff" : "#6b7280",
                      }}>
                        {count}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}

            {/* ── States ── */}
            {loading && (
              <div style={styles.center}>
                <div style={styles.spinner} />
                <p style={{ color: "#6b7280", marginTop: 12 }}>{t(lang,"loading")}</p>
              </div>
            )}
            {error && (
              <div style={styles.center}>
                <p style={{ color: "#ef4444" }}>⚠️ {error}</p>
              </div>
            )}
            {!loading && !error && filteredBookings.length === 0 && (
              <div style={styles.center}>
                <p style={{ fontSize: 40, marginBottom: 8 }}>🏨</p>
                <p style={{ color: "#9ca3af", fontSize: 14 }}>
                  {search               ? t(lang,"noSearchResult",search) :
                   activeFilter !== "All" ? t(lang,"noCategoryItems") :
                   dateMode     !== "All" ? t(lang,"noPeriodItems") :
                                            t(lang,"noBookings")}
                </p>
              </div>
            )}

            {/* ── Booking list ── */}
            {!loading && !error && filteredBookings.map((b) => {
              const roomType = b.roomType;
              const roomUnit = b.roomUnit;
              const cfg      = STATUS_CONFIG[b.status] || { color: "#374151", bg: "#f3f4f6", dot: "#9ca3af" };
              const statusLabel = b.status ? (t(lang, b.status) !== b.status ? t(lang, b.status) : b.status) : "?";
              const imageUrl = roomType?.image ? `${API}/uploads/${roomType.image}` : null;
              const nights   = b.checkIn && b.checkOut
                ? Math.round((new Date(b.checkOut) - new Date(b.checkIn)) / 86400000) : 0;

              const guestDisplay = isStaff
                ? (`${b.user?.firstname ?? ""} ${b.user?.lastname ?? ""}`.trim() || b.user?.email || b.guestName || "ไม่ระบุชื่อ")
                : null;

              return (
                <div
                  key={b._id}
                  className="sp-booking-item"
                  style={styles.bookingItem}
                  onClick={() => goToDetail(b._id)}
                  onMouseEnter={(e) => { e.currentTarget.style.background = "#f9fafb"; e.currentTarget.style.cursor = "pointer"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                >
                  <div className="sp-img-wrap" style={styles.imgWrap}>
                    {imageUrl
                      ? <img src={imageUrl} alt="ห้องพัก" style={styles.roomImg} />
                      : <div style={styles.imgPlaceholder}>🛏️</div>
                    }
                  </div>

                  <div style={styles.roomInfo}>
                    <h3 style={styles.roomTitle}>
                      {t(lang, "roomNoLabel")} {roomUnit ? roomUnit.roomNumber : "-"}
                      {roomType ? ` (${roomType.name})` : ""}
                    </h3>

                    {isStaff && guestDisplay && (
                      <div style={styles.guestChip}>
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
                        </svg>
                        {guestDisplay}
                      </div>
                    )}

                    <Row icon="📅" label={t(lang,"bookingDate")}  value={fmtDateTime(b.createdAt)} />
                    <Row icon="🏨" label={t(lang,"checkInDate")}  value={fmtDate(b.checkIn)} />
                    <Row icon="🚪" label={t(lang,"checkOutDate")} value={fmtDate(b.checkOut)} />
                    {nights > 0 && <Row icon="🌙" label={t(lang,"nightsCount")} value={`${nights} ${t(lang,"nightsUnit")}`} />}
                    {b.totalAmount != null && (
                      <Row icon="💰" label={t(lang,"total")} value={`฿${b.totalAmount.toLocaleString()}`} />
                    )}
                  </div>

                  <div className="sp-status-col" style={styles.statusCol}>
                    <span style={{ ...styles.statusBadge, background: cfg.bg, color: cfg.color }}>
                      <span style={{ ...styles.statusDot, background: cfg.dot }} />
                      {statusLabel}
                    </span>
                    <button
                      style={styles.detailsBtn}
                      onClick={(e) => { e.stopPropagation(); goToDetail(b._id); }}
                    >
                      {t(lang,"details")}
                    </button>
                  </div>
                </div>
              );
            })}

          </div>
        </main>
      </div>
    </>
  );
}

function Row({ icon, label, value }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 2 }}>
      <span style={{ fontSize: 12, width: 16, textAlign: "center" }}>{icon}</span>
      <span style={{ fontSize: 13, color: "#6b7280", minWidth: 80 }}>{label} :</span>
      <span style={{ fontSize: 13, color: "#111827", fontWeight: 500 }}>{value}</span>
    </div>
  );
}

const styles = {
  page:        { minHeight: "100vh", background: "#f0f0f0", fontFamily: "'Noto Sans Thai', sans-serif", display: "flex", flexDirection: "column" },
  main:        { flex: 1, padding: "0 24px 24px" },
  card:        { background: "rgba(255,255,255,0.55)", backdropFilter: "blur(20px) saturate(180%)", WebkitBackdropFilter: "blur(20px) saturate(180%)", border: "1px solid rgba(255,255,255,0.75)", borderRadius: 16, padding: "28px 32px", minHeight: 480, boxShadow: "0 8px 32px rgba(0,0,0,0.10), 0 1px 0 rgba(255,255,255,0.8) inset" },
  cardHeader:  { display: "flex", alignItems: "center", gap: 10, marginBottom: 16 },
  cardTitle:   { margin: 0, fontSize: 24, fontWeight: 700, color: "#111" },
  totalBadge:  { marginLeft: "auto", background: "#f3f4f6", color: "#374151", fontSize: 12, fontWeight: 700, padding: "3px 12px", borderRadius: 999 },
  divider:     { height: 1, background: "#e5e7eb", marginBottom: 20 },
  center:      { display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "60px 0" },

  spinner:     { width: 28, height: 28, border: "3px solid #e5e7eb", borderTopColor: "#1a1a1a", borderRadius: "50%", animation: "spin .7s linear infinite" },

  searchWrap:  { position: "relative", marginBottom: 16, maxWidth: 340 },
  searchIcon:  { position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "#9ca3af", pointerEvents: "none" },
  searchInput: { width: "100%", padding: "9px 36px", borderRadius: 8, border: "1px solid #e5e7eb", fontSize: 13, fontFamily: "inherit", outline: "none", boxSizing: "border-box", background: "#f9fafb" },
  searchClear: { position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "#9ca3af", fontSize: 13, padding: 0 },

  dateBar:      { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 12 },
  dateBarLabel: { fontSize: 12, color: "#6b7280", fontWeight: 600 },
  dateInput:    { padding: "6px 12px", borderRadius: 999, border: "1px solid #e5e7eb", background: "#f9fafb", color: "#111827", fontSize: 13, fontFamily: "'Noto Sans Thai', sans-serif", outline: "none", cursor: "pointer" },

  filterBar:   { display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 20 },
  filterTab:   { display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 14px", borderRadius: 999, border: "1px solid", cursor: "pointer", fontSize: 13, transition: "all .15s", fontFamily: "inherit", outline: "none" },
  filterDot:   { width: 7, height: 7, borderRadius: "50%", flexShrink: 0 },
  filterCount: { fontSize: 11, borderRadius: 999, padding: "1px 6px" },

  bookingItem:    { display: "flex", alignItems: "flex-start", gap: 20, padding: "18px 12px", borderBottom: "1px solid #f3f4f6", borderRadius: 8, transition: "background .15s" },
  imgWrap:        { flexShrink: 0, width: 140, height: 100, borderRadius: 8, overflow: "hidden", background: "#f3f4f6" },
  roomImg:        { width: "100%", height: "100%", objectFit: "cover" },
  imgPlaceholder: { width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 36, color: "#9ca3af" },
  roomInfo:       { flex: 1, display: "flex", flexDirection: "column" },
  roomTitle:      { margin: "0 0 8px", fontSize: 16, fontWeight: 700, color: "#111" },
  guestChip:      { display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12, fontWeight: 600, color: "#1e40af", background: "#dbeafe", padding: "2px 9px", borderRadius: 999, marginBottom: 8, width: "fit-content" },
  statusCol:      { display: "flex", flexDirection: "column", alignItems: "flex-end", justifyContent: "space-between", minHeight: 100, flexShrink: 0 },
  statusBadge:    { display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 600, padding: "4px 10px", borderRadius: 999, whiteSpace: "nowrap" },
  statusDot:      { width: 7, height: 7, borderRadius: "50%", flexShrink: 0 },
  detailsBtn:     { background: "none", border: "none", cursor: "pointer", fontSize: 13, color: "#9ca3af", padding: 0, textDecoration: "underline", textUnderlineOffset: 2 },
};